/**
 * La finestra delle impostazioni di un widget (la rotellina nel menu ⋮).
 * Le scelte si applicano alla bozza della dashboard: diventano definitive
 * solo con "Salva", come tutto il resto della modifica.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { UserRole } from '../../types/roles';
import { CATALOGO, MAX_TESTO_NOTA, MAX_TITOLO_NOTA, type MappaImpostazioni } from '../../lib/dashboard/catalogo';
import { collegamentiDisponibili } from '../../lib/dashboard/collegamenti';
import type { WidgetIstanza } from '../../lib/dashboard/layout';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { titoloWidget } from './titolo';

interface Props {
  widget: WidgetIstanza | null;
  ruolo: UserRole;
  onChiudi: () => void;
  onApplica: (id: string, impostazioni: unknown) => void;
}

type Valori = Record<string, unknown>;

function Scelta({ etichetta, valore, opzioni, onCambia }: {
  etichetta: string;
  valore: unknown;
  opzioni: ReadonlyArray<{ valore: string | number; testo: string }>;
  onCambia: (v: string | number) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-gray-700">{etichetta}</span>
      <select
        value={String(valore)}
        onChange={(e) => {
          const scelta = opzioni.find((o) => String(o.valore) === e.target.value);
          if (scelta) onCambia(scelta.valore);
        }}
        className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
      >
        {opzioni.map((o) => <option key={String(o.valore)} value={String(o.valore)}>{o.testo}</option>)}
      </select>
    </label>
  );
}

function Spunta({ etichetta, valore, onCambia }: { etichetta: string; valore: unknown; onCambia: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" checked={valore === true} onChange={(e) => onCambia(e.target.checked)} className="h-4 w-4 accent-primary-600" />
      {etichetta}
    </label>
  );
}

function Campi({ widget, valori, imposta, ruolo }: { widget: WidgetIstanza; valori: Valori; imposta: (chiave: string, v: unknown) => void; ruolo: UserRole }): ReactNode {
  switch (widget.tipo) {
    case 'fatturato':
      return (
        <>
          <Scelta etichetta="Periodo" valore={valori.periodo} onCambia={(v) => imposta('periodo', v)}
            opzioni={[{ valore: 'mese', testo: 'Il mese in corso' }, { valore: 'trimestre', testo: 'Il trimestre in corso' }, { valore: 'anno', testo: "L'anno in corso" }]} />
          <Spunta etichetta="Confronta con lo stesso periodo dell'anno prima" valore={valori.confronto} onCambia={(v) => imposta('confronto', v)} />
        </>
      );
    case 'andamento-mensile':
      return <Scelta etichetta="Quanti mesi" valore={valori.mesi} onCambia={(v) => imposta('mesi', v)}
        opzioni={[{ valore: 6, testo: 'Ultimi 6 mesi' }, { valore: 12, testo: 'Ultimi 12 mesi' }]} />;
    case 'nuovi-lead':
      return <Scelta etichetta="Periodo" valore={valori.periodo} onCambia={(v) => imposta('periodo', v)}
        opzioni={[{ valore: 'settimana', testo: 'Ultimi 7 giorni' }, { valore: 'mese', testo: 'Ultimi 30 giorni' }]} />;
    case 'lead-per-fonte':
      return <Scelta etichetta="Periodo" valore={valori.periodo} onCambia={(v) => imposta('periodo', v)}
        opzioni={[{ valore: 'mese', testo: 'Ultimi 30 giorni' }, { valore: 'anno', testo: "Ultimo anno" }, { valore: 'sempre', testo: 'Da sempre' }]} />;
    case 'lead-da-richiamare':
      return <Spunta etichetta="Solo i lead assegnati a me" valore={valori.soloMiei} onCambia={(v) => imposta('soloMiei', v)} />;
    case 'offerte-aperte':
      return <Spunta etichetta="Solo le offerte che ho creato io" valore={valori.soloMie} onCambia={(v) => imposta('soloMie', v)} />;
    case 'azioni-rapide': {
      const scelti = (valori.collegamenti as string[]) ?? [];
      const inverti = (id: string) => imposta('collegamenti', scelti.includes(id) ? scelti.filter((x) => x !== id) : [...scelti, id]);
      return (
        <div className="space-y-2">
          <p className="text-sm text-neutral-600">Scegli le sezioni da mostrare, nell&apos;ordine in cui le spunti. Se non ne scegli nessuna vedi quelle di partenza.</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {collegamentiDisponibili(ruolo).map((c) => (
              <Spunta key={c.id} etichetta={c.titolo} valore={scelti.includes(c.id)} onCambia={() => inverti(c.id)} />
            ))}
          </div>
        </div>
      );
    }
    case 'nota':
      return (
        <>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-gray-700">Titolo</span>
            <Input value={String(valori.titolo ?? '')} maxLength={MAX_TITOLO_NOTA} onChange={(e) => imposta('titolo', e.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-gray-700">Testo</span>
            <Textarea rows={6} value={String(valori.testo ?? '')} maxLength={MAX_TESTO_NOTA} onChange={(e) => imposta('testo', e.target.value)} />
            <span className="block text-right text-[11px] text-neutral-400">{String(valori.testo ?? '').length}/{MAX_TESTO_NOTA} · visibile solo a te</span>
          </label>
        </>
      );
    default:
      return <p className="text-sm text-neutral-500">Questo widget non ha impostazioni.</p>;
  }
}

export function ImpostazioniWidget({ widget, ruolo, onChiudi, onApplica }: Props) {
  const [valori, setValori] = useState<Valori>({});

  useEffect(() => {
    if (widget) setValori({ ...(widget.impostazioni as MappaImpostazioni[typeof widget.tipo]) });
  }, [widget]);

  if (!widget) return null;
  const imposta = (chiave: string, v: unknown) => setValori((prima) => ({ ...prima, [chiave]: v }));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onChiudi(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titoloWidget(widget)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-neutral-500">{CATALOGO[widget.tipo].descrizione}</p>
          <Campi widget={widget} valori={valori} imposta={imposta} ruolo={ruolo} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onChiudi}>Annulla</Button>
          <Button className="bg-primary-600 text-white hover:bg-primary-700" onClick={() => { onApplica(widget.id, valori); onChiudi(); }}>
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
